import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import { useLanguage } from "./LanguageContext";
import { useAdminData, type FAQItem } from "./admin/AdminDataContext";
import { useTheme } from "./ThemeInjector";

function getFaqText(item: FAQItem, field: "q" | "a", lang: string): string {
  const map = item[field];
  if (lang === "en") return map.en;
  const translated = map[lang as keyof typeof map];
  return (translated as string) || map.en;
}

export function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const { lang, t } = useLanguage();
  const { siteConfig } = useAdminData();
  const theme = useTheme();

  if (!siteConfig.features?.faq) return null;

  const faqItems = siteConfig.faqItems || [];
  if (faqItems.length === 0) return null;

  return (
    <section id="faq" className="py-24 px-6 max-w-3xl mx-auto">
      <div className="text-center mb-16">
        <p
          className="tracking-[0.3em] uppercase mb-3"
          style={{ fontFamily: theme.fontBody, fontSize: "12px", color: theme.accent }}
        >
          {t("faq.label")}
        </p>
        <h2
          className="tracking-[0.1em]"
          style={{ fontFamily: theme.fontHeading, fontSize: "clamp(28px, 4vw, 42px)", fontWeight: 400, color: theme.fg }}
        >
          {t("faq.title")}
        </h2>
      </div>

      <div className="space-y-3">
        {faqItems.map((item, idx) => (
          <div
            key={item.id}
            className="bg-white rounded-xl overflow-hidden shadow-[0_2px_16px_rgba(40,32,20,0.04)]"
          >
            <button
              onClick={() => setOpenIndex(openIndex === idx ? null : idx)}
              className="w-full px-6 py-5 flex items-center justify-between text-left focus:outline-none focus:ring-2 focus:ring-inset focus:ring-[#C7A76C] rounded-xl"
              aria-expanded={openIndex === idx}
            >
              <span
                className="tracking-[0.03em] pr-4"
                style={{ fontFamily: theme.fontHeading, fontSize: "18px", fontWeight: 500, color: theme.fg }}
              >
                {getFaqText(item, "q", lang)}
              </span>
              <motion.div
                animate={{ rotate: openIndex === idx ? 180 : 0 }}
                transition={{ duration: 0.2 }}
                className="shrink-0"
                style={{ color: theme.accent }}
              >
                <ChevronDown size={18} strokeWidth={1.5} />
              </motion.div>
            </button>
            <AnimatePresence>
              {openIndex === idx && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.25 }}
                  className="overflow-hidden"
                >
                  <div className="px-6 pb-5">
                    <p
                      className="leading-[1.9]"
                      style={{ fontFamily: theme.fontBody, fontSize: "14px", fontWeight: 300, color: theme.muted }}
                    >
                      {getFaqText(item, "a", lang)}
                    </p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        ))}
      </div>
    </section>
  );
}